import { Coupon, CouponCodec } from '@/models/Coupon'
import { LoggedInUser } from '@/models/User'
import router, { RouteName } from '@/router'
import store from '@/store'
import axios, { AxiosInstance } from 'axios'
import { isLeft, isRight } from 'fp-ts/lib/Either'
import { identity } from 'fp-ts/lib/function'
import * as T from 'io-ts'
import { formatValidationErrors } from 'io-ts-reporters'

const ApiErrorCodec = T.type({
  success: T.literal(false),
  message: T.string,
})

export interface RedeemCouponRequest {
  checkNumber: number
  subtotal: number
  discount: number
  guestCount: number
  comments: string
}

class Api {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({ baseURL: process.env.VUE_APP_API_URL })
    this.client.interceptors.request.use(x => {
      x.headers = {
        ...x.headers,
        authorization: `Bearer ${store.state.State.s.user?.token}`,
      }
      return x
    })
    this.client.interceptors.response.use(identity, error => {
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          if (router.currentRoute.name !== RouteName.signIn) {
            store.commit('signOut')
            router.push({ name: RouteName.signIn })
          }
        }
        const decoded = ApiErrorCodec.decode(error.response?.data)
        if (isRight(decoded)) {
          throw decoded.right.message
        }
      }
    })
  }

  async signIn(username: string, password: string): Promise<LoggedInUser> {
    return (await this.client.post(`/login`, { username, password })).data.data
  }

  async getCoupon(id: string): Promise<Coupon> {
    const either = CouponCodec.decode((await this.client.get(`/coupons/${id}`)).data.data)
    if (isLeft(either)) {
      throw formatValidationErrors(either.left)
    }
    return either.right
  }

  async redeemCoupon(id: string, request: RedeemCouponRequest): Promise<void> {
    if ((await this.client.post(`/coupons/${id}/redeem`, request)) == null) {
      throw 'Failed to redeem offer'
    }
  }

  async getRedemptionHistory(min: Date, max: Date): Promise<[Coupon[], boolean]> {
    const res = await this.client.get(`/redemptionHistory`, {
      params: new URLSearchParams({
        start: Math.round(min.getTime() / 1000).toString(),
        end: Math.round(max.getTime() / 1000).toString(),
      }),
    })
    const either = T.array(CouponCodec).decode(res.data.data.coupons)
    if (isLeft(either)) {
      throw formatValidationErrors(either.left)
    }
    return [either.right, res.data.data.hasMore]
  }
}

export default new Api()
